import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export const PUBLIC_ENTITY_HEADER_CLEARANCE_CLASS = {
  /** 120px org logo overlap (`PublicEntityHero`). */
  heroLogo: 'pt-[72px]',
  /** Larger public profile avatar overlap. */
  heroAvatar: 'pt-12 md:pt-14',
  none: '',
} as const

export type PublicEntityHeaderClearance = keyof typeof PUBLIC_ENTITY_HEADER_CLEARANCE_CLASS

export function publicEntityHeaderClearanceClass(
  clearance: PublicEntityHeaderClearance = 'heroLogo',
): string {
  return PUBLIC_ENTITY_HEADER_CLEARANCE_CLASS[clearance]
}

type PublicEntityHeaderProps = {
  title: ReactNode
  actions?: ReactNode
  children?: ReactNode
  className?: string
  clearance?: PublicEntityHeaderClearance
  /** Vertical rhythm between title row and metadata children. */
  density?: 'default' | 'compact'
  /** Align Copy link with a single-line title (name + pill). */
  titleAlign?: 'start' | 'center'
}

function PublicEntityHeaderTitle({ title }: { title: ReactNode }) {
  if (typeof title === 'string') {
    return (
      <h1 className="min-w-0 flex-1 basis-full text-3xl font-semibold tracking-tight md:basis-0">
        {title}
      </h1>
    )
  }
  return <div className="min-w-0 flex-1 basis-full md:basis-0">{title}</div>
}

/** Title + optional actions below a public entity hero. */
export function PublicEntityHeader({
  title,
  actions,
  children,
  className,
  clearance = 'heroLogo',
  density = 'default',
  titleAlign = 'start',
}: PublicEntityHeaderProps) {
  return (
    <header
      className={cn(
        density === 'compact' ? 'space-y-2' : 'space-y-4',
        publicEntityHeaderClearanceClass(clearance),
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-wrap justify-between gap-4',
          titleAlign === 'center' ? 'items-center' : 'items-start',
        )}
      >
        <PublicEntityHeaderTitle title={title} />
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 max-md:w-full">
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </header>
  )
}
